import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Search,
  BarChart3,
  TrendingUp,
  Target,
  Lightbulb,
} from "lucide-react";
import type { BlogPost } from "@shared/schema";
import {
  BLOG_CATEGORIES,
  getCategoryBySlug,
  getPostsByCategory,
  searchPosts,
} from "@/lib/blog-categories";

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  "startup-finance": <BarChart3 size={28} />,
  "growth-metrics": <TrendingUp size={28} />,
  "market-research": <Target size={28} />,
  "founder-strategy": <Lightbulb size={28} />,
};

export default function BlogCategory() {
  const params = useParams<{ slug: string }>();
  const category = getCategoryBySlug(params.slug);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: posts, isLoading } = useQuery<BlogPost[]>({
    queryKey: ["/api/posts", "published"],
    queryFn: async () => {
      const res = await fetch("/api/posts?published=true");
      if (!res.ok) throw new Error("Failed to fetch posts");
      return res.json();
    },
  });

  if (!category) {
    return (
      <>
        <Navbar />
        <main id="main-content" className="pt-20 min-h-screen">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-24 text-center">
            <h1 className="text-3xl font-display font-bold mb-4">Category Not Found</h1>
            <p className="text-muted-foreground mb-8">This category does not exist.</p>
            <Link href="/blog">
              <Button>Back to Blog</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryPosts = posts ? getPostsByCategory(posts, category.slug) : [];
  const filteredPosts = searchQuery ? searchPosts(categoryPosts, searchQuery) : categoryPosts;

  const otherCategories = BLOG_CATEGORIES.filter((c) => c.slug !== category.slug);

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
            <Link href="/blog">
              <Button variant="ghost" className="mb-8 text-muted-foreground hover:text-foreground -ml-4" data-testid="link-back-blog">
                <ArrowLeft size={16} className="mr-2" /> Back to Blog
              </Button>
            </Link>

            <div className="mb-12">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-primary/10 rounded-xl text-primary">
                  {CATEGORY_ICONS[category.slug]}
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl md:text-5xl font-display font-bold">{category.name}</h1>
                </div>
              </div>
              <p className="text-xl text-muted-foreground max-w-2xl mt-2">{category.description}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {category.subcategories.map((sub) => (
                  <Badge key={sub} variant="outline" className="border-white/20 text-muted-foreground text-xs">
                    {sub}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Search within category */}
            <div className="mb-8">
              <div className="relative max-w-lg">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  data-testid="input-search-category"
                  type="text"
                  aria-label={`Search ${category.name} articles`}
                  placeholder={`Search ${category.name} articles...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-11 py-5 bg-card border-white/10 text-sm rounded-xl focus:border-primary/50"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm"
                    aria-label="Clear search"
                    data-testid="button-clear-category-search"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-2" role="status" aria-live="polite">
                {filteredPosts.length} article{filteredPosts.length !== 1 ? "s" : ""}
                {searchQuery ? ` matching "${searchQuery}"` : ""}
              </p>
            </div>

            {isLoading ? (
              <div className="space-y-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-card border border-white/5 rounded-2xl p-8 animate-pulse">
                    <div className="h-4 bg-white/5 rounded w-1/4 mb-4" />
                    <div className="h-8 bg-white/5 rounded w-3/4 mb-4" />
                    <div className="h-4 bg-white/5 rounded w-full" />
                  </div>
                ))}
              </div>
            ) : filteredPosts.length > 0 ? (
              <div className="space-y-6">
                {filteredPosts.map((post) => (
                  <Link key={post.id} href={`/blog/${post.slug}`}>
                    <article
                      data-testid={`card-post-${post.id}`}
                      className="bg-card border border-white/5 rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 cursor-pointer group"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <Calendar size={14} className="text-muted-foreground" aria-hidden="true" />
                        <time className="text-sm text-muted-foreground">
                          Last Updated:{" "}
                          {new Date(post.updatedAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </time>
                      </div>
                      <h2 className="text-2xl font-display font-bold mb-3 group-hover:text-primary transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-muted-foreground leading-relaxed mb-6">{post.excerpt}</p>
                      <span className="text-primary font-medium text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                        Read Article <ArrowRight size={16} />
                      </span>
                    </article>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-card border border-white/5 rounded-2xl">
                <Search size={32} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-display font-semibold mb-3">No articles found</h3>
                <p className="text-muted-foreground">
                  {searchQuery
                    ? `No results matching "${searchQuery}". Try a different search term.`
                    : "No articles in this category yet."}
                </p>
                {searchQuery && (
                  <Button variant="outline" className="mt-4" onClick={() => setSearchQuery("")}>
                    Clear Search
                  </Button>
                )}
              </div>
            )}

            {/* Other Categories */}
            <nav className="mt-16" aria-label="Explore other blog categories">
              <h2 className="text-xl font-display font-bold mb-6">Explore Other Topics</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {otherCategories.map((cat) => {
                  const count = posts ? getPostsByCategory(posts, cat.slug).length : 0;
                  return (
                    <Link key={cat.slug} href={`/blog/${cat.slug}`}>
                      <div
                        data-testid={`hub-${cat.slug}`}
                        className="bg-card border border-white/10 rounded-xl p-6 hover:border-primary/30 transition-all group cursor-pointer"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="text-primary">{CATEGORY_ICONS[cat.slug]}</div>
                          <h3 className="font-display font-semibold group-hover:text-primary transition-colors">
                            {cat.name}
                          </h3>
                        </div>
                        <p className="text-sm text-muted-foreground">{count} articles</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </nav>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
